"use client";

import { usePathname } from "next/navigation";
import type { JSX } from "react";
import { HorizontalCarousel, Icon, Link, View } from "@/components";
import type { LandingNavItem } from "@/types/elearning/landing";
import cn from "@/utils/cn";

export interface LandingNavProps {
  /** Pestañas en orden; los ítems `external` abren en pestaña nueva y nunca se marcan activos. */
  items: LandingNavItem[];
}

/**
 * @name LandingNav
 * @description Navegación por pestañas de la landing pública. Queda sticky
 * debajo del Nav institucional global (fixed, 84px: `min-h-[84px]` es
 * border-box, o sea que su borde inferior de 6px ya va incluido) y resalta la
 * pestaña
 * activa según la ruta. En pantallas angostas los ítems se desplazan
 * horizontalmente con el HorizontalCarousel del template.
 *
 * @param {LandingNavProps} props - Los ítems de navegación.
 * @returns {JSX.Element} La barra de pestañas renderizada.
 */
const LandingNav = ({ items }: LandingNavProps): JSX.Element => {
  const pathname = usePathname();

  const isActive = (item: LandingNavItem): boolean =>
    !item.external &&
    (pathname === item.href || pathname.startsWith(`${item.href}/`));

  return (
    <View
      type="nav"
      role="navigation"
      className="sticky top-[84px] z-[100] bg-background/95 dark:bg-card/95 backdrop-blur border-b border-border"
    >
      <View className="max-w-5xl mx-auto px-4 sm:px-6">
        <HorizontalCarousel>
          {items.map((item) => {
            const active = isActive(item);
            return (
              <Link
                key={item.href}
                href={item.href}
                target={item.external ? "_blank" : undefined}
                rel={item.external ? "noopener noreferrer" : undefined}
                aria-current={active ? "page" : undefined}
                className={cn(
                  "shrink-0 whitespace-nowrap inline-flex items-center gap-1.5 px-3 py-3 text-sm border-b-2 transition-colors",
                  active
                    ? "border-navbar-border text-primary dark:text-foreground font-semibold"
                    : "border-transparent text-muted-foreground hover:text-foreground"
                )}
              >
                {item.label}
                {item.external && (
                  // El `!` no es decorativo: el átomo Icon y la regla `i` de
                  // globals.css imponen un piso de 25px, con lo cual un
                  // `text-xs` a secas queda inerte y el glifo sale a 25px al
                  // lado de un texto de 14px. Bajarlo es seguro acá: el glifo
                  // es decorativo (el átomo le pone aria-hidden porque no lleva
                  // aria-label) y el área táctil es toda la pestaña.
                  <Icon
                    name="arrow-up-right-from-square"
                    className="text-xs!"
                  />
                )}
              </Link>
            );
          })}
        </HorizontalCarousel>
      </View>
    </View>
  );
};

export default LandingNav;
